// libs
import React, { useState, memo, useRef, useMemo, useEffect } from 'react';
import isEmpty from 'lodash/isEmpty';

// components
import SizeChart from './components/SizeChart';
import SizeButton from './components/SizeButton';
import MeasureWidth from 'shared/components/MeasureWidth';
import SizeGuideBottomSheet from './components/SizeGuideBottomSheet/SizeGuideBottomSheet';
import SelectedSizeChart from 'shared/components/SelectedSizeChart';

// hooks
import { useSelectedSKUSizeGuide } from 'shared/store/details/hooks/selectors';
import useTheme from '@nykaa/ui-components/themes/hooks/useTheme';

// types
import { Theme } from '@nykaa/ui-components/themes/types';

// constants
import {
  TEXTS,
  TRACKING_KEY,
  FEW_LEFT_LEGEND,
  SOLD_OUT_LEGEND,
  WIDTH_OFFSET,
  SIZE_SECTION_V2_EXPERIMENT,
  WINDOW_SCROLL_OFFSET,
  SCROLL_DELAY,
} from './texts';

// analytics
import { onReferSizeGuideClickTracking, trackSizeGuideChartClosed } from './analytics';

// styles
import {
  SizeOptionContainer,
  SizeBtnContainer,
  SizeGuideButton,
  TitleContainerV2,
  SecondaryTitleV2,
  SizeGuideText,
  LegendDot,
  Legend,
  LegendsContainer,
  ArrowRightIcon,
} from './styles';

// helpers
import {
  getSizeOptionsTrackingData,
  updateDataLayerOnSizeSelect,
} from 'shared/components/SizeSelector/helper';
import { useSSRExperimentVariant } from '@nykaa/experiment-sdk/store/hooks';
import { trackSizeSelection } from './helpers';

export interface ISizeOptionProps {
  sizeOptions: ProductDetail.IProduct['sizeOptions'];
  onSelect: (size: ProductDetail.ISizeOption) => void;
  isOneSize: boolean;
  shake?: boolean;
  currentSize?: ProductDetail.ISizeOption;
  isNewSizeChartEnabled?: boolean;
  isMobile?: boolean;
  sizeGuideDataAvailable?: boolean;
}

const TABLE_SCROLL_OFFSET = 150;

const SizeOptions = ({
  sizeOptions,
  onSelect,
  isOneSize,
  shake,
  currentSize,
  isNewSizeChartEnabled = false,
  isMobile = false,
  sizeGuideDataAvailable = false,
}: ISizeOptionProps) => {
  const [openSizeChart, setOpenSizeChart] = useState(false);
  const [measuredWidth, setMeasureWidth] = useState(0);
  const sizeGuideURL = useSelectedSKUSizeGuide();
  const contRef = useRef<HTMLDivElement>(null);
  const theme: Theme = useTheme();
  const sizeTrackingData = useRef({
    selectedSizesList: [],
    selectedSize: '',
  });
  const { isEligible: isMeasurementChartV2 = false } = useSSRExperimentVariant(
    SIZE_SECTION_V2_EXPERIMENT
  );
  const { title, options } = sizeOptions;
  const sizeChartRef = useRef(null);

  useEffect(() => {
    //size should be auto selected in case of a single sku
    if (options.length === 1) onSelect(options[0]);
  }, [onSelect, options]);

  const sizeDataForAllSizes = useMemo(() => {
    let sizeData = {},
      conversionCount = 0;
    sizeOptions.options.map((optionsData) => {
      sizeData[optionsData.sku] = sizeOptions?.meta?.units?.reduce((acc, unitsData) => {
        acc[unitsData.label] = sizeOptions?.meta?.columns?.reduce((innerAcc, columnsData) => {
          if (columnsData.conversion === true) conversionCount += 1;
          return {
            ...innerAcc,
            [columnsData.id]: {
              type: columnsData.type,
              value: optionsData?.size_data && optionsData.size_data[columnsData.id][unitsData.id],
              label:
                columnsData.conversion === true
                  ? columnsData.label + ' (' + unitsData.label + ')'
                  : columnsData.label,
            },
          };
        }, {});

        return acc;
      }, {});
    });
    return { sizeData: sizeData, showToggle: conversionCount !== sizeOptions.options.length };
  }, [sizeOptions?.meta?.columns, sizeOptions?.meta?.units, sizeOptions?.options]);

  if (isEmpty(sizeOptions) || isOneSize) {
    return null;
  }

  const openSizeGuideChart = () => {
    onReferSizeGuideClickTracking(TRACKING_KEY);
    setOpenSizeChart(true);
  };

  const onSizeOptionClick = (sizeOption: ProductDetail.ISizeOption) => {
    const { availableSizes, outOfStockSizes } = getSizeOptionsTrackingData(sizeOptions?.options);
    let sizesList: string[] = sizeTrackingData.current.selectedSizesList;

    if (isMeasurementChartV2) {
      setTimeout(() => {
        const bottomPosition =
          sizeChartRef.current?.getBoundingClientRect().bottom + window.pageYOffset;
        const scrollToPosition = bottomPosition - window.innerHeight + TABLE_SCROLL_OFFSET;

        if (
          window.innerHeight - sizeChartRef.current?.getBoundingClientRect().bottom <
          WINDOW_SCROLL_OFFSET
        ) {
          window.scrollTo({
            top: scrollToPosition,
            behavior: 'smooth',
          });
        }
      }, SCROLL_DELAY);
    }

    sizesList.push(sizeOption?.sizeName);
    sizeTrackingData.current = {
      selectedSizesList: sizesList,
      selectedSize: sizeOption?.sizeName,
    };
    trackSizeSelection(sizeTrackingData.current);
    updateDataLayerOnSizeSelect(availableSizes, outOfStockSizes, sizeOption);
    onSelect(sizeOption);
  };

  const checkStockStatus = () => {
    let sizeNameArray: string[] = [];
    const stockStatus = options.some((val) => {
      return val.stock_remaining;
    });
    const isOutOfStock = options.some((val) => {
      return val.isOutOfStock;
    });
    options.map((val) => {
      sizeNameArray.push(val.sizeName);
    });
    return {
      stockStatus: stockStatus,
      isOutOfStock: isOutOfStock,
      greatestString: sizeNameArray.sort((a, b) => {
        return b.length - a.length;
      })[0],
    };
  };

  let showSizeGuideButton = isNewSizeChartEnabled
    ? sizeGuideDataAvailable
    : isEmpty(sizeGuideURL) === false;

  const handleSizeGuideClose = () => {
    setOpenSizeChart(false);
    trackSizeGuideChartClosed();
  };

  return (
    <SizeOptionContainer data-test="size-options-v2" shake={shake}>
      <TitleContainerV2>
        <SecondaryTitleV2>{title}</SecondaryTitleV2>
        <LegendsContainer>
          {checkStockStatus()?.isOutOfStock && (
            <Legend>
              <LegendDot />
              <span>{SOLD_OUT_LEGEND}</span>
            </Legend>
          )}
          {checkStockStatus()?.stockStatus && (
            <Legend>
              <LegendDot isFewLeft={true} />
              <span>{FEW_LEFT_LEGEND}</span>
            </Legend>
          )}
        </LegendsContainer>
      </TitleContainerV2>
      <SizeBtnContainer ref={contRef}>
        <MeasureWidth onMeasured={setMeasureWidth}>
          <SizeButton
            onClick={() => {}}
            isSelected={false}
            isOutOfStock={false}
            size={checkStockStatus()?.greatestString}
            containerRef={contRef}
            stockRemaining={4}
            isSizeButtonV2={true}
          />
        </MeasureWidth>

        {measuredWidth !== 0 &&
          options.map((sizeOption, i) => {
            const isSelected = currentSize !== undefined && currentSize.sku === sizeOption.sku;
            return (
              <SizeButton
                key={i}
                onClick={() => onSizeOptionClick(sizeOption)}
                isSelected={isSelected}
                isOutOfStock={sizeOption.isOutOfStock === 1}
                size={sizeOption.sizeName}
                containerRef={contRef}
                stockRemaining={sizeOption.stock_remaining}
                finalWidth={measuredWidth - WIDTH_OFFSET}
                isSizeButtonV2={true}
              />
            );
          })}
      </SizeBtnContainer>
      {isMeasurementChartV2 &&
        currentSize?.sku &&
        !isEmpty(
          sizeDataForAllSizes?.sizeData && sizeDataForAllSizes.sizeData[currentSize?.sku]
        ) && (
          <div ref={sizeChartRef}>
            <SelectedSizeChart
              sizeData={sizeDataForAllSizes.sizeData[currentSize?.sku]}
              sizeName={currentSize?.sizeName}
              isOutOfStock={checkStockStatus().isOutOfStock}
              showToggle={sizeDataForAllSizes.showToggle}
            />
          </div>
        )}
      <SizeGuideText>
        {showSizeGuideButton && (
          <SizeGuideButton
            onClick={openSizeGuideChart}
            data-test="btn-size-chart"
            isSizeSelectorV2={true}
          >
            {TEXTS.SIZE_GUIDE}
            <ArrowRightIcon size={theme.spacing.spacing80} color={theme.colors.secondary} />
          </SizeGuideButton>
        )}
      </SizeGuideText>

      {!isNewSizeChartEnabled && openSizeChart && (
        <SizeChart sizeGuideURL={sizeGuideURL} onRemove={handleSizeGuideClose} />
      )}

      {isNewSizeChartEnabled && sizeGuideDataAvailable && (
        <SizeGuideBottomSheet
          isMobile={isMobile}
          showSizeChart={openSizeChart}
          onClose={handleSizeGuideClose}
        />
      )}
    </SizeOptionContainer>
  );
};

export default memo(SizeOptions);
